import React, { useState, useEffect } from "react";
import { Mail, Phone, Lock, X, CheckCircle } from "lucide-react";
import { useProfile } from "../../context/ProfileContext";

const Settings = () => {
  const {
    profile,
    setProfile,
    sendOtp,
    verifyEmailOtp,
    verifyMobileOtp,
    changePassword,
    deleteAccount,
  } = useProfile();

  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [isVerified, setIsVerified] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));

  // ✅ Populate profile details
  useEffect(() => {
    if (profile?.email) setEmail(profile.email);
    if (profile?.mobile) {
      const parts = profile.mobile.split(" ");
      if (parts.length === 2) {
        setCountryCode(parts[0]);
        setMobile(parts[1]);
      } else {
        setMobile(profile.mobile);
      }
    }
  }, [profile]);

  // ✅ Password validation function
  const validatePassword = (pwd, confirmPwd) => {
    if (!pwd && !confirmPwd) return "Password is required";
    if (pwd !== confirmPwd) return "Passwords do not match";

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    if (!passwordRegex.test(pwd)) {
      return "Password must be at least 8 characters, include uppercase, lowercase, number, and special character";
    }

    return "";
  };

  // ✅ Handle save (email / phone / password)
  const handleSave = async (type) => {
    if (type === "email") {
      const res = await sendOtp({ email }, "email");
      if (res.status === 200) {
        setModalType("email");
        setShowModal(true);
      } else {
        alert(res.message || "Failed to send OTP");
      }
    } else if (type === "phone") {
      const fullMobile = `${countryCode} ${mobile}`;
      const res = await sendOtp({ mobile: fullMobile }, "phone");
      if (res.status === 200) {
        setModalType("phone");
        setShowModal(true);
      } else {
        alert(res.message || "Failed to send OTP");
      }
    } else if (type === "password") {
      const error = validatePassword(password, confirmPassword);
      setPasswordError(error);
      if (error) return;

      const res = await changePassword(password, confirmPassword);
      if (res.status === 200) {
        alert("Password updated successfully");
        setPassword("");
        setConfirmPassword("");
        setPasswordError("");
      } else {
        alert(res.message || "Failed to change password");
      }
    }
  };

  // ✅ Confirm OTP
  const handleConfirmCode = async () => {
    const otpCode = otp.join("");
    if (otpCode.length !== 6) return alert("Enter all 6 digits");

    let res;
    if (modalType === "email") {
      res = await verifyEmailOtp({ email, otp: otpCode });
    } else if (modalType === "phone") {
      res = await verifyMobileOtp({ mobile: `${countryCode} ${mobile}`, otp: otpCode });
    }

    if (res?.status === 200) {
      setIsVerified(true);
      setProfile((prev) => ({
        ...prev,
        ...(modalType === "email" ? { email } : { mobile: `${countryCode} ${mobile}` }),
      }));

      setTimeout(() => {
        setShowModal(false);
        setIsVerified(false);
        setOtp(Array(6).fill(""));
      }, 2000);
    } else {
      alert(res?.message || "Invalid OTP");
    }
  };

  // OTP input
  const handleOtpChange = (val, idx) => {
    if (/^[0-9]?$/.test(val)) {
      const newOtp = [...otp];
      newOtp[idx] = val;
      setOtp(newOtp);
    }
  };

  return (
    <>
      {/* Main Settings Card */}
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-8 space-y-6 relative z-10">
        <h2 className="text-lg font-semibold text-gray-700">Notification</h2>

        {/* Change Email & Mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Email */}
          <div className="border rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Change Email Address
            </label>
            <div className="flex items-center space-x-2 border-b border-gray-300 pb-2 mb-4">
              <Mail className="text-gray-400 w-5 h-5" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full focus:outline-none text-gray-700"
              />
            </div>
            <button
              onClick={() => handleSave("email")}
              className="bg-green-500 hover:bg-green-600 text-white w-full py-2 rounded-lg"
            >
              Save
            </button>
          </div>

          {/* Mobile */}
          <div className="border rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Change Mobile Number
            </label>
            <div className="flex items-center border-b border-gray-300 pb-2 mb-4">
              <Phone className="text-gray-400 w-5 h-5 mr-2" />
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="border-none bg-transparent text-gray-700 focus:outline-none"
              >
                <option value="+1">+1 (US)</option>
                <option value="+91">+91 (India)</option>
                <option value="+44">+44 (UK)</option>
                <option value="+61">+61 (Australia)</option>
                <option value="+81">+81 (Japan)</option>
              </select>
              <input
                type="tel"
                value={mobile}
                onChange={(e) => {
                  // Allow only numbers
                  const onlyNumbers = e.target.value.replace(/\D/g, "");
                  setMobile(onlyNumbers);
                }}
                className="flex-1 focus:outline-none text-gray-700 ml-2"
                placeholder="Enter mobile number"
              />
            </div>

            <button
              onClick={() => handleSave("phone")}
              className="bg-green-500 hover:bg-green-600 text-white w-full py-2 rounded-lg"
            >
              Save
            </button>
          </div>
        </div>

        {/* Change Password */}
        <div className="border rounded-xl p-4">
          <label className="block text-sm font-medium text-gray-600 mb-4">
            Change Password
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
            <div className="flex items-center space-x-2 border-b border-gray-300 pb-2">
              <Lock className="text-gray-400 w-5 h-5" />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError(validatePassword(e.target.value, confirmPassword));
                }}
                className="w-full focus:outline-none text-gray-700"
              />
            </div>
            <div className="flex items-center space-x-2 border-b border-gray-300 pb-2">
              <Lock className="text-gray-400 w-5 h-5" />
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setPasswordError(validatePassword(password, e.target.value));
                }}
                className="w-full focus:outline-none text-gray-700"
              />
            </div>
          </div>
          {passwordError && (
            <p className="text-red-500 text-sm mb-2">{passwordError}</p>
          )}
          <button
            onClick={() => handleSave("password")}
            className={`w-full py-2 rounded-lg text-white ${
              passwordError
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-500 hover:bg-green-600"
            }`}
            disabled={!!passwordError}
          >
            Save
          </button>
        </div>

        {/* Delete Account */}
        <div className="text-center">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="text-red-500 font-medium hover:underline"
          >
            Delete Account
          </button>
        </div>
      </div>

      {/* Verification Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-lg flex items-center justify-center z-50">
          <div className="bg-white w-[400px] sm:w-[450px] md:w-[500px] rounded-2xl shadow-lg p-6 relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            {isVerified ? (
              <div className="flex flex-col items-center justify-center space-y-4 py-6">
                <CheckCircle className="text-green-500 w-14 h-14" />
                <h2 className="text-lg font-semibold text-gray-800">
                  {modalType === "email"
                    ? "Email Updated Successfully!"
                    : "Mobile Number Updated Successfully!"}
                </h2>
                <p className="text-sm text-gray-500">Closing in 2 seconds...</p>
              </div>
            ) : (
              <>
                <h2 className="text-center text-lg font-semibold text-gray-800 mb-2">
                  Verification Code
                </h2>
                <p className="text-center text-sm text-gray-500 mb-4">
                  Enter the 6-digit code we sent to{" "}
                  <span className="text-blue-500 font-medium">
                    {modalType === "email" ? email : mobile}
                  </span>
                </p>

                <div className="flex justify-center space-x-3 mb-6">
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleOtpChange(e.target.value, i)}
                      className="w-10 h-12 text-center border-b-2 border-gray-300 focus:border-green-500 text-lg focus:outline-none"
                    />
                  ))}
                </div>

                <button
                  onClick={handleConfirmCode}
                  className="bg-green-500 hover:bg-green-600 text-white w-full py-2 rounded-lg mb-3"
                >
                  Confirm
                </button>

                <p className="text-center">
                  <button
                    onClick={() => handleSave(modalType)}
                    className="text-red-500 text-sm hover:underline"
                  >
                    Resend Code
                  </button>
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-lg flex items-center justify-center z-50">
          <div className="bg-white w-[390px] sm:w-[400px] rounded-2xl shadow-lg p-8 text-center">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Are you sure you want to Delete Account?
            </h2>
            <div className="flex justify-center gap-4">
              <button
                onClick={async () => {
                  const res = await deleteAccount();
                  if (res.status === 200) {
                    alert("Account deleted successfully");
                  } else {
                    alert(res.message || "Failed to delete account");
                  }
                  setShowDeleteConfirm(false);
                }}
                className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg"
              >
                Yes
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded-lg"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Settings;
